import React from "react";
import { DAILY_REVENUE, MONTHLY_REVENUE, CATEGORIES } from "../lib/data";
import { CORPORATE_ACCOUNTS, CUSTOMER_SEGMENTS, SHOPEE_SEARCH_TRENDS, FULFILLMENT_QUEUE, FULFILLMENT_STATS } from "../lib/dataUseCases";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import { formatPeso, formatNumber, pct, cn } from "../lib/utils";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid, RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";
import { useChartColors } from "../lib/chartColors";
import { TrendingUp, TrendingDown, Sparkles, Package, Clock, AlertCircle } from "lucide-react";

const tickStyle = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

function TT({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg">
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono-tabular">
          <span className="w-2 h-2" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-muted">{p.name}</span>
          <span className="ml-auto font-semibold">{formatter ? formatter(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

const CHANNEL_METRICS = [
  { channel: "Physical Retail", revenue: 92_400_000, grossMargin: 0.318, transactions: 38_420, aov: 2405, color: "var(--chart-1)" },
  { channel: "Shopee", revenue: 24_200_000, grossMargin: 0.246, transactions: 18_180, aov: 1331, color: "var(--chart-2)" },
  { channel: "Direct / WhatsApp", revenue: 18_800_000, grossMargin: 0.341, transactions: 4_200, aov: 4476, color: "var(--chart-3)" },
];

const SHOPEE_FUNNEL = [
  { stage: "Listing Views", value: 1_842_000 },
  { stage: "Product Page", value: 412_000 },
  { stage: "Add to Cart", value: 96_400 },
  { stage: "Checkout", value: 38_100 },
  { stage: "Orders", value: 18_180 },
];

const CATEGORY_MIX = CATEGORIES.map((c, i) => ({
  name: c.name.split(" ")[0],
  Physical: [38_400_000, 32_200_000, 14_900_000, 6_900_000][i],
  Shopee: [9_600_000, 8_800_000, 4_100_000, 1_700_000][i],
  Direct: [7_300_000, 6_900_000, 3_200_000, 1_400_000][i],
}));

export default function Omnichannel() {
  const C = useChartColors();
  const total = CHANNEL_METRICS.reduce((s, c) => s + c.revenue, 0);
  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART V · Channel Analytics" title="Omnichannel Sales"
        description="Compare physical, Shopee and direct channels — revenue, margin, conversion and AOV." />

      <div className="tile grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
        <Agg label="Total MTD Revenue" value={formatPeso(total, { compact: true })} />
        <Agg label="Shopee Conversion" value="4.1%" status="info" />
        <Agg label="Online Fill Rate" value="96.1%" status="success" />
        <Agg label="Blended GM" value="30.8%" status="success" />
      </div>

      <div className="grid grid-cols-12 gap-6">
        {CHANNEL_METRICS.map(c => (
          <Tile key={c.channel} className="col-span-12 md:col-span-4" testid={`channel-${c.channel.split(" ")[0]}`}
                title={c.channel} action={<Badge status="neutral">{((c.revenue / total) * 100).toFixed(0)}% share</Badge>}>
            <div className="grid grid-cols-2 gap-4">
              <Stat label="Revenue" value={formatPeso(c.revenue, { compact: true })} />
              <Stat label="Gross Margin" value={pct(c.grossMargin)} status={c.grossMargin >= 0.3 ? "success" : "warning"} />
              <Stat label="Transactions" value={formatNumber(c.transactions)} />
              <Stat label="Avg Order" value={formatPeso(c.aov)} />
            </div>
            <div className="mt-4">
              <div className="kpi-label mb-2">Revenue Share</div>
              <div className="h-2 bg-surface-2 rounded-sm overflow-hidden">
                <div className="h-full" style={{ width: `${(c.revenue / total) * 100}%`, background: `rgb(${c.color})` }} />
              </div>
            </div>
          </Tile>
        ))}

        <Tile className="col-span-12 lg:col-span-7" testid="category-channel-mix" title="Revenue by Category × Channel">
          <div style={{ height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={CATEGORY_MIX} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="name" tick={tickStyle} stroke={C.borderStrong} />
                <YAxis tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `₱${(v / 1e6).toFixed(0)}M`} />
                <Tooltip content={<TT formatter={v => formatPeso(v, { compact: true })} />} cursor={{ fill: C.surface2 }} />
                <Bar dataKey="Physical" stackId="a" fill={C.chart[0]} isAnimationActive={false} />
                <Bar dataKey="Shopee" stackId="a" fill={C.chart[1]} isAnimationActive={false} />
                <Bar dataKey="Direct" stackId="a" fill={C.chart[2]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Tile>

        <Tile className="col-span-12 lg:col-span-5" title="Shopee Conversion Funnel — MTD">
          <div className="space-y-2 mt-1">
            {SHOPEE_FUNNEL.map((s, i) => {
              const top = SHOPEE_FUNNEL[0].value;
              const w = (s.value / top) * 100;
              const prev = i > 0 ? SHOPEE_FUNNEL[i - 1].value : null;
              const conversion = prev ? (s.value / prev) * 100 : null;
              return (
                <div key={s.stage}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium">{s.stage}</span>
                    <span className="font-mono-tabular">
                      {formatNumber(s.value)}
                      {conversion !== null && <span className="text-muted ml-2">{conversion.toFixed(1)}%</span>}
                    </span>
                  </div>
                  <div className="h-7 bg-surface-2 rounded-sm overflow-hidden relative">
                    <div className="h-full transition-all" style={{ width: `${w}%`, background: `linear-gradient(90deg, rgb(var(--chart-2)), rgb(var(--chart-2) / 0.7))` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <hr className="my-3 border-border" />
          <div className="text-[11px] text-muted leading-relaxed">
            <strong className="text-fg">Insight:</strong> Listing→PDP at 22% is healthy. PDP→ATC at 23% is below category benchmark — recommend A+ creative + UGC.
          </div>
        </Tile>

        <Tile className="col-span-12" title="Channel Revenue — Last 30 Days">
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={DAILY_REVENUE} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" />
                <XAxis dataKey="date" tick={tickStyle} stroke={C.borderStrong} />
                <YAxis tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `₱${(v / 1e6).toFixed(0)}M`} />
                <Tooltip content={<TT formatter={v => formatPeso(v, { compact: true })} />} />
                <Area type="monotone" dataKey="physical" stackId="1" stroke={C.chart[0]} fill={C.chartAlpha(1, 0.5)} isAnimationActive={false} />
                <Area type="monotone" dataKey="shopee" stackId="1" stroke={C.chart[1]} fill={C.chartAlpha(2, 0.5)} isAnimationActive={false} />
                <Area type="monotone" dataKey="direct" stackId="1" stroke={C.chart[2]} fill={C.chartAlpha(3, 0.5)} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Tile>

        {/* Corporate Account Revenue Intelligence */}
        <Tile className="col-span-12 lg:col-span-7" testid="corporate-accounts"
              title="Corporate Account Revenue Intelligence — B2B"
              action={<div className="flex items-center gap-1.5">
                <Badge status="critical">{CORPORATE_ACCOUNTS.filter(a => a.signal === "at-risk").length} at-risk</Badge>
                <Badge status="success">{CORPORATE_ACCOUNTS.filter(a => a.signal === "upsell").length} upsell</Badge>
                <Badge status="info">{CORPORATE_ACCOUNTS.filter(a => a.signal === "new").length} new</Badge>
              </div>}>
          <div className="max-h-[420px] overflow-y-auto -mx-4">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface z-10">
                <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                  <th className="py-2 px-3 text-left">Account</th>
                  <th className="py-2 px-3 text-right">YTD ₱</th>
                  <th className="py-2 px-3 text-right">YoY</th>
                  <th className="py-2 px-3 text-right">Orders/Qtr</th>
                  <th className="py-2 px-3 text-left">Signal</th>
                  <th className="py-2 px-3 text-left">Recommended Action</th>
                </tr>
              </thead>
              <tbody className="font-mono-tabular">
                {CORPORATE_ACCOUNTS.map(a => (
                  <tr key={a.id} data-testid={`acc-${a.id}`} className="border-b border-border hover:bg-surface-2">
                    <td className="py-2 px-3 font-sans">
                      <div className="font-medium text-sm">{a.name}</div>
                      <div className="text-[10px] text-muted">{a.segment}</div>
                    </td>
                    <td className="py-2 px-3 text-right">{formatPeso(a.ytdRevenue, { compact: true })}</td>
                    <td className={cn("py-2 px-3 text-right font-semibold", a.growth === null ? "text-muted" : a.growth >= 0.05 ? "text-success" : a.growth <= -0.05 ? "text-critical" : "text-warning")}>
                      {a.growth === null ? "—" : `${a.growth >= 0 ? "+" : ""}${(a.growth * 100).toFixed(0)}%`}
                    </td>
                    <td className="py-2 px-3 text-right">{a.freq}</td>
                    <td className="py-2 px-3 font-sans">
                      <Badge status={a.signal === "at-risk" ? "critical" : a.signal === "upsell" ? "success" : a.signal === "new" ? "info" : "neutral"}>{a.signal}</Badge>
                    </td>
                    <td className="py-2 px-3 font-sans text-[11px] text-muted max-w-md">{a.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Tile>

        {/* Customer Segments */}
        <Tile className="col-span-12 lg:col-span-5" testid="customer-segments"
              title="Customer Segmentation & Behavioral Analytics"
              action={<Badge status="info">RFM clustered</Badge>}>
          <div className="space-y-2">
            {CUSTOMER_SEGMENTS.map((s, i) => {
              const top = Math.max(...CUSTOMER_SEGMENTS.map(x => x.ytdRevenue));
              const w = (s.ytdRevenue / top) * 100;
              return (
                <div key={s.id} data-testid={`segment-${s.id}`} className="p-2.5 border border-border rounded-sm bg-surface-2/40">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{s.name}</span>
                    <span className="font-mono-tabular text-xs text-primary">{formatPeso(s.ytdRevenue, { compact: true })}</span>
                  </div>
                  <div className="h-1.5 bg-surface-2 rounded-sm overflow-hidden mb-1.5">
                    <div className="h-full" style={{ width: `${w}%`, background: `rgb(var(--chart-${(i % 5) + 1}))` }} />
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-muted font-mono-tabular">
                    <span>{formatNumber(s.size)} accts · ₱{formatNumber(s.basket)} avg · {s.freq}/yr</span>
                  </div>
                  <div className="text-[11px] text-fg/80 mt-1">→ {s.action}</div>
                </div>
              );
            })}
          </div>
        </Tile>

        {/* Shopee Search Trends */}
        <Tile className="col-span-12 lg:col-span-7" testid="shopee-search-trends"
              title="Shopee Search Trends — Emerging Queries"
              action={<Badge status="warning">{SHOPEE_SEARCH_TRENDS.filter(t => !t.hasListing).length} demand gaps</Badge>}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                <th className="py-2 px-3 text-left">Query</th>
                <th className="py-2 px-3 text-right">Monthly Searches</th>
                <th className="py-2 px-3 text-right">Growth</th>
                <th className="py-2 px-3 text-right">Conv</th>
                <th className="py-2 px-3 text-center">Catalog?</th>
                <th className="py-2 px-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="font-mono-tabular">
              {SHOPEE_SEARCH_TRENDS.map((t, i) => (
                <tr key={i} className="border-b border-border" data-testid={`search-${i}`}>
                  <td className="py-2 px-3 font-sans italic text-fg/90">"{t.query}"</td>
                  <td className="py-2 px-3 text-right">{formatNumber(t.searches)}</td>
                  <td className={cn("py-2 px-3 text-right font-semibold", t.growth >= 1 ? "text-success" : t.growth >= 0.5 ? "text-info" : "text-fg")}>
                    +{(t.growth * 100).toFixed(0)}%
                  </td>
                  <td className="py-2 px-3 text-right">{t.hasListing ? `${(t.conversion * 100).toFixed(1)}%` : "—"}</td>
                  <td className="py-2 px-3 text-center font-sans">
                    {t.hasListing ? <Badge status="success">Listed</Badge> : <Badge status="critical">Gap</Badge>}
                  </td>
                  <td className="py-2 px-3 font-sans text-[11px] text-muted">
                    {!t.hasListing ? "Add SKU + Shopee listing" : t.conversion < 0.05 ? "Optimize listing (creative + price)" : "Sustain"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tile>

        {/* Shopee Fulfillment Hub */}
        <Tile className="col-span-12 lg:col-span-5" testid="fulfillment-hub"
              title="Shopee Fulfillment Hub — Live Queue"
              action={<div className="flex items-center gap-1.5">
                <Badge status="critical">{FULFILLMENT_QUEUE.filter(o => o.atRisk).length} at risk</Badge>
                <Badge status="info">{FULFILLMENT_STATS.fillRate24h * 100}% fill</Badge>
              </div>}>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <Stat label="Today" value={formatNumber(FULFILLMENT_STATS.ordersToday, { compact: true })} />
            <Stat label="Cycle (min)" value={FULFILLMENT_STATS.avgCycleMinutes} />
            <Stat label="Returns" value={`${(FULFILLMENT_STATS.returnRate * 100).toFixed(1)}%`} />
            <Stat label="Hubs" value="3" />
          </div>
          <div className="max-h-[300px] overflow-y-auto -mx-4">
            <div className="space-y-0 divide-y divide-border">
              {FULFILLMENT_QUEUE.slice(0, 12).map(o => (
                <div key={o.orderId} className="px-4 py-2 flex items-center gap-2 text-xs" data-testid={`order-${o.orderId}`}>
                  <span className={cn("w-1.5 h-1.5 rounded-full",
                    o.atRisk ? "bg-critical animate-pulse" : o.status === "dispatched" ? "bg-success" : "bg-info")} />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-tabular text-[11px]">{o.orderId}</div>
                    <div className="text-[10px] text-muted truncate">{o.sku} · {o.units}u · {o.hub}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge status={o.atRisk ? "critical" : o.status === "dispatched" ? "success" : "neutral"}>{o.status}</Badge>
                    <div className={cn("text-[10px] font-mono-tabular mt-0.5", o.atRisk ? "text-critical" : "text-muted")}>
                      {o.minutesLeft < 0 ? "OVERDUE" : `${o.minutesLeft}m left`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Tile>
      </div>
    </div>
  );
}

function Agg({ label, value, status }) {
  return (
    <div className="px-4 py-3">
      <div className="kpi-label flex items-center gap-2">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-2xl mt-1.5">{value}</div>
    </div>
  );
}

function Stat({ label, value, status }) {
  return (
    <div>
      <div className="kpi-label flex items-center gap-1.5">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-lg mt-1">{value}</div>
    </div>
  );
}
